import React, { useMemo, useState, useEffect } from "react";
import styled from "styled-components";
import {
  Modal,
  Card,
  Row,
  Col,
  Select,
  Button,
  Statistic,
  message,
  Tooltip,
} from "antd";
import {
  AccountBookOutlined,
  DollarOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { getDebugMode } from "../helpers/constants";
import { getUnitDisplayAddress } from "../lib/unitAddressUtils";
import { downloadScheduleETaxPacketExcel } from "../utils/scheduleEExcelExport";
import { useBreakpoints } from "../lib/breakpoints";

/**
 * Normalize a Firestore Timestamp or date string to Date.
 */
const toDate = (v) => {
  if (!v) return null;
  if (v.toDate && typeof v.toDate === "function") return v.toDate();
  if (v instanceof Date) return v;
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
};

/** Format unit address (string or object) for display. */
const formatUnitAddress = (unit) => {
  if (!unit) return "";
  return getUnitDisplayAddress(unit) || unit.name || unit.id || "";
};

/**
 * Build tax summary from real payments (paid only, cash basis).
 * payments: array of payment objects (with dueDate, paidDate, amount, type, unitId, propertyId?, tenantId, tenantName, unitName, method, status, etc.)
 * units: array of { id, name, address, propertyId? }
 * taxYear: 'last12' | '2025' | '2024' etc.
 * unitIdFilter: 'all' | unit id
 * properties: optional array of { id, address, stripeConnectIdOverride?, stripeConnectIdDestination? } for entity/LLC resolution
 * connectAccounts: optional array of { id, name, stripeConnectId, isPrimary } for entity labels
 */
const buildTaxSummaryFromPayments = (
  payments = [],
  units = [],
  taxYear,
  unitIdFilter,
  properties = [],
  connectAccounts = []
) => {
  const now = new Date();

  let startDate;
  let endDate;
  let taxYearLabel;
  if (taxYear === "last12") {
    endDate = new Date(now);
    startDate = new Date(now);
    startDate.setFullYear(startDate.getFullYear() - 1);
    startDate.setDate(startDate.getDate() + 1);
    taxYearLabel = "Last 12 Months";
  } else {
    const y = parseInt(taxYear, 10);
    startDate = new Date(y, 0, 1);
    endDate = new Date(y, 11, 31, 23, 59, 59, 999);
    taxYearLabel = String(y);
  }

  const inRange = (d) => d && d >= startDate && d <= endDate;
  const unitById = (id) => (units || []).find((u) => u.id === id);

  const primaryAccount = (connectAccounts || []).find((a) => a.isPrimary);
  const primaryConnectId = primaryAccount?.stripeConnectId || null;
  /** Resolve deposit account name for a payment: property override, else unit override, else primary. */
  const getEntityNameForPayment = (propertyId, unit) => {
    if (!connectAccounts?.length) return null;
    const prop =
      propertyId && (properties || []).length
        ? (properties || []).find((p) => p.id === propertyId)
        : null;
    const connectId =
      prop?.stripeConnectIdDestination ??
      prop?.stripeConnectIdOverride ??
      unit?.stripeConnectIdOverride ??
      primaryConnectId;
    if (!connectId) return null;
    const acc = (connectAccounts || []).find(
      (a) => a.stripeConnectId === connectId
    );
    return acc?.name ?? "Primary account";
  };

  // Only include paid payments (cash basis - income when received)
  const filtered = payments.filter((p) => {
    if (p.status !== "paid") return false;
    const paid = toDate(p.paidDate);
    const due = toDate(p.dueDate);
    const dateForRange = paid || due;
    if (!dateForRange || !inRange(dateForRange)) return false;
    if (unitIdFilter && unitIdFilter !== "all" && p.unitId !== unitIdFilter)
      return false;
    return true;
  });

  let totalRent = 0;
  let totalFees = 0;
  const typeToCategory = {
    rent: "Rent Income",
    late_fee: "Late Fee Income",
    utility: "Other Income",
    other: "Other Income",
    security_deposit: "Security Deposit",
  };

  const MONTH_NAMES = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const entityByProperty = {};

  const transactions = filtered.map((p) => {
    const amount = Number(p.amount) || 0;
    const type = (p.type || "rent").toLowerCase();
    if (type === "rent") totalRent += amount;
    else totalFees += amount;

    const paid = toDate(p.paidDate);
    const due = toDate(p.dueDate);
    const dateReceived = paid
      ? paid.toISOString().slice(0, 10)
      : due
      ? due.toISOString().slice(0, 10)
      : "";
    const category = typeToCategory[type] || "Other Income";
    const description =
      p.notes ||
      (type === "rent"
        ? `Rent - ${p.unitName || "Door"}`
        : type === "late_fee"
        ? "Late fee"
        : category);

    const unit = unitById(p.unitId);
    const propertyAddress = formatUnitAddress(unit) || p.unitName || "";
    const propertyKey = propertyAddress || p.unitName || "Unknown Property";
    const propertyId = p.propertyId || unit?.propertyId;
    const depositAccountName =
      getEntityNameForPayment(propertyId, unit) ?? null;
    if (propertyKey && depositAccountName != null)
      entityByProperty[propertyKey] = depositAccountName;

    const rentPeriod = due
      ? `${MONTH_NAMES[due.getMonth()]} ${due.getFullYear()}`
      : "";

    return {
      taxYear: taxYearLabel,
      dateReceived,
      dueDate: due ? due.toISOString().slice(0, 10) : "",
      propertyOrAddress: p.unitName || propertyAddress || "",
      propertyAddress,
      unitName: p.unitName || "",
      tenantName: p.tenantName || "",
      category,
      description,
      amount,
      paymentMethod: p.method || "",
      status: p.status || "",
      paymentId: p.id || "",
      transactionId: p.transactionId || "",
      notes: p.notes || "",
      rentPeriod,
      currency: "USD",
      depositAccountName: depositAccountName ?? undefined,
    };
  });

  const totalIncome = totalRent + totalFees;
  const netRevenue = totalIncome;

  return {
    total_income: totalIncome,
    net_revenue: netRevenue,
    breakdown: { rent: totalRent, fees: totalFees },
    transactions,
    entityByProperty:
      Object.keys(entityByProperty).length > 0 ? entityByProperty : undefined,
    taxYearLabel,
  };
};

/**
 * Demo/marketing mock data for Tax Summaries when ?debug=true.
 * Pro landlord scenario: 3 properties with realistic income and expenses.
 */
const DEMO_PROPERTIES = ["123 Maple Ave", "405 Oak St", "789 Pine Ln"];

const getDemoTaxPivot = (unitIdFilter) => {
  // Income by property: Rents Received, Late Fees
  const rents = {
    "123 Maple Ave": 18000,
    "405 Oak St": 24500,
    "789 Pine Ln": 15600,
  };
  const lateFees = { "123 Maple Ave": 0, "405 Oak St": 150, "789 Pine Ln": 0 };
  // Expenses by property
  const cleaning = { "123 Maple Ave": 0, "405 Oak St": 350, "789 Pine Ln": 0 };
  const insurance = {
    "123 Maple Ave": 800,
    "405 Oak St": 1200,
    "789 Pine Ln": 600,
  };
  const repairs = {
    "123 Maple Ave": 450,
    "405 Oak St": 1200,
    "789 Pine Ln": 0,
  };
  const supplies = { "123 Maple Ave": 0, "405 Oak St": 0, "789 Pine Ln": 0 };
  const taxes = {
    "123 Maple Ave": 2400,
    "405 Oak St": 3200,
    "789 Pine Ln": 1800,
  };
  const utilities = { "123 Maple Ave": 0, "405 Oak St": 0, "789 Pine Ln": 0 };
  const otherExpenses = {
    "123 Maple Ave": 0,
    "405 Oak St": 0,
    "789 Pine Ln": 0,
  };

  const propIdToAddress = {
    "demo-maple": "123 Maple Ave",
    "demo-oak": "405 Oak St",
    "demo-pine": "789 Pine Ln",
  };

  const propsToInclude =
    unitIdFilter && unitIdFilter !== "all"
      ? [propIdToAddress[unitIdFilter] || unitIdFilter].filter(Boolean)
      : DEMO_PROPERTIES;

  const incomeRows = [
    { category: "Rents Received", amounts: rents },
    { category: "Late Fees", amounts: lateFees },
    {
      category: "Other Income",
      amounts: { "123 Maple Ave": 0, "405 Oak St": 0, "789 Pine Ln": 0 },
    },
    {
      category: "Security Deposits",
      amounts: { "123 Maple Ave": 0, "405 Oak St": 0, "789 Pine Ln": 0 },
    },
  ];
  const expenseRows = [
    { category: "Cleaning", amounts: cleaning },
    { category: "Insurance", amounts: insurance },
    { category: "Repairs", amounts: repairs },
    { category: "Supplies", amounts: supplies },
    { category: "Taxes", amounts: taxes },
    { category: "Utilities", amounts: utilities },
    { category: "Other Expenses", amounts: otherExpenses },
  ];

  const rows = [];
  incomeRows.forEach((r) => {
    const amounts = {};
    let total = 0;
    propsToInclude.forEach((prop) => {
      const amt = r.amounts[prop] ?? 0;
      amounts[prop] = amt;
      total += amt;
    });
    rows.push({ category: r.category, amounts, total });
  });

  expenseRows.forEach((r) => {
    const amounts = {};
    let total = 0;
    propsToInclude.forEach((prop) => {
      const amt = r.amounts[prop] ?? 0;
      amounts[prop] = amt;
      total += amt;
    });
    rows.push({ category: r.category, amounts, total });
  });

  const totalIncome = rows
    .filter((r) =>
      [
        "Rents Received",
        "Late Fees",
        "Other Income",
        "Security Deposits",
      ].includes(r.category)
    )
    .reduce((s, r) => s + r.total, 0);
  const totalExpenses = rows
    .filter((r) =>
      [
        "Cleaning",
        "Insurance",
        "Repairs",
        "Supplies",
        "Taxes",
        "Utilities",
        "Other Expenses",
      ].includes(r.category)
    )
    .reduce((s, r) => s + r.total, 0);

  const totalIncomeByProp = {};
  const totalExpensesByProp = {};
  const netByProp = {};
  propsToInclude.forEach((prop) => {
    totalIncomeByProp[prop] = incomeRows.reduce(
      (s, r) => s + (r.amounts[prop] ?? 0),
      0
    );
    totalExpensesByProp[prop] = expenseRows.reduce(
      (s, r) => s + (r.amounts[prop] ?? 0),
      0
    );
    netByProp[prop] = totalIncomeByProp[prop] - totalExpensesByProp[prop];
  });

  rows.push({
    category: "Total Income",
    amounts: totalIncomeByProp,
    total: totalIncome,
    isSubtotal: true,
  });
  rows.push({
    category: "Total Expenses",
    amounts: totalExpensesByProp,
    total: totalExpenses,
    isSubtotal: true,
  });
  rows.push({
    category: "Net Income (Loss)",
    amounts: netByProp,
    total: totalIncome - totalExpenses,
    isSubtotal: true,
  });

  return { rows, properties: propsToInclude };
};

const getDemoTaxSummaryStats = (unitIdFilter) => {
  const { rows } = getDemoTaxPivot(unitIdFilter);
  const totalIncomeRow = rows.find((r) => r.category === "Total Income");
  const netRow = rows.find((r) => r.category === "Net Income (Loss)");
  const rentsRow = rows.find((r) => r.category === "Rents Received");
  const feesRow = rows.find((r) => r.category === "Late Fees");
  return {
    total_income: totalIncomeRow?.total ?? 0,
    net_revenue: netRow?.total ?? 0,
    breakdown: { rent: rentsRow?.total ?? 0, fees: feesRow?.total ?? 0 },
  };
};

/** Generate mock transactions from demo pivot for detail ledger export. */
const getDemoTransactions = (unitIdFilter) => {
  const { rows, properties } = getDemoTaxPivot(unitIdFilter);
  const transactions = [];
  const taxYearLabel = "2025";
  properties.forEach((prop) => {
    rows.forEach((r) => {
      const amt = r.amounts[prop];
      if (amt && amt > 0 && !r.isSubtotal) {
        transactions.push({
          taxYear: taxYearLabel,
          dateReceived: "2025-01-15",
          dueDate: "2025-01-01",
          propertyAddress: prop,
          propertyOrAddress: prop,
          unitName: prop,
          tenantName: "Tenant",
          category: r.category,
          description: `${r.category} - ${prop}`,
          amount: amt,
          currency: "USD",
          paymentMethod: "ach",
          status: "paid",
          notes: "",
          paymentId: `demo-${prop}-${r.category}`,
          transactionId: "",
          rentPeriod: "January 2025",
        });
      }
    });
  });
  return transactions;
};

/** Map transaction category to Schedule E row label. */
const TRANSACTION_TO_SCHEDULE_E_ROW = {
  "Rent Income": "Rents Received",
  "Late Fee Income": "Late Fees",
  "Other Income": "Other Income",
  "Security Deposit": "Security Deposits",
};

/**
 * Build Schedule E Summary pivot: Category | Property1 | Property2 | ... | Grand Total
 * Aggregates transactions by property and Schedule E category.
 * Optionally includes entityByProperty: { propertyKey: entityName } for deposit account/LLC labels.
 */
const buildScheduleESummaryPivot = (transactions, entityByProperty) => {
  const propertyKeys = new Set();
  const pivot = {}; // { category: { propertyKey: amount } }

  const getPropertyKey = (t) => {
    const addr = t.propertyAddress || t.propertyOrAddress || t.unitName;
    return addr || "Unknown Property";
  };

  transactions.forEach((t) => {
    const propKey = getPropertyKey(t);
    propertyKeys.add(propKey);
    const scheduleERow =
      TRANSACTION_TO_SCHEDULE_E_ROW[t.category] || "Other Income";
    if (!pivot[scheduleERow]) pivot[scheduleERow] = {};
    pivot[scheduleERow][propKey] =
      (pivot[scheduleERow][propKey] || 0) + (Number(t.amount) || 0);
  });

  const properties = Array.from(propertyKeys).sort();
  const rows = [];

  // Income rows (only include those with data, plus standard expense rows for structure)
  const incomeRows = [
    "Rents Received",
    "Late Fees",
    "Other Income",
    "Security Deposits",
  ];
  const expenseRows = [
    "Cleaning",
    "Insurance",
    "Repairs",
    "Supplies",
    "Taxes",
    "Utilities",
    "Other Expenses",
  ];

  incomeRows.forEach((row) => {
    const amounts = {};
    let rowTotal = 0;
    properties.forEach((prop) => {
      const amt = pivot[row]?.[prop] || 0;
      amounts[prop] = amt;
      rowTotal += amt;
    });
    rows.push({ category: row, amounts, total: rowTotal });
  });

  expenseRows.forEach((row) => {
    const amounts = {};
    let rowTotal = 0;
    properties.forEach((prop) => {
      const amt = pivot[row]?.[prop] || 0;
      amounts[prop] = amt;
      rowTotal += amt;
    });
    rows.push({ category: row, amounts, total: rowTotal });
  });

  const totalIncome = incomeRows.reduce(
    (sum, r) => sum + (rows.find((x) => x.category === r)?.total || 0),
    0
  );
  const totalExpenses = expenseRows.reduce(
    (sum, r) => sum + (rows.find((x) => x.category === r)?.total || 0),
    0
  );

  const totalIncomeByProp = {};
  const totalExpensesByProp = {};
  const netByProp = {};
  properties.forEach((prop) => {
    totalIncomeByProp[prop] = incomeRows.reduce(
      (s, r) => s + (pivot[r]?.[prop] || 0),
      0
    );
    totalExpensesByProp[prop] = expenseRows.reduce(
      (s, r) => s + (pivot[r]?.[prop] || 0),
      0
    );
    netByProp[prop] = totalIncomeByProp[prop] - totalExpensesByProp[prop];
  });

  rows.push({
    category: "Total Income",
    amounts: totalIncomeByProp,
    total: totalIncome,
    isSubtotal: true,
  });
  rows.push({
    category: "Total Expenses",
    amounts: totalExpensesByProp,
    total: totalExpenses,
    isSubtotal: true,
  });
  rows.push({
    category: "Net Income (Loss)",
    amounts: netByProp,
    total: totalIncome - totalExpenses,
    isSubtotal: true,
  });

  const result = { rows, properties };
  if (entityByProperty && Object.keys(entityByProperty).length > 0)
    result.entityByProperty = entityByProperty;
  return result;
};

const escapeCsvCell = (v) => {
  const str = v == null ? "" : String(v);
  return str.includes(",") || str.includes('"') || str.includes("\n")
    ? `"${str.replace(/"/g, '""')}"`
    : str;
};

const downloadScheduleESummaryFromPivot = (pivot, yearLabel) => {
  const { rows, properties } = pivot;
  if (!rows?.length || !properties?.length) {
    message.warning("No data to export.");
    return;
  }
  const header = ["Category", ...properties, "Grand Total"]
    .map(escapeCsvCell)
    .join(",");
  const csvRows = rows.map((row) => {
    const cells = [row.category];
    properties.forEach((prop) => {
      cells.push(row.amounts[prop] ?? (row.isSubtotal ? "" : 0));
    });
    cells.push(row.total);
    return cells.map(escapeCsvCell).join(",");
  });
  const csv = [header, ...csvRows].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `Schedule-E-Summary-${yearLabel}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};

const downloadScheduleESummaryCsv = (transactions, yearLabel) => {
  if (!transactions || !transactions.length) {
    message.warning("No transactions to export.");
    return;
  }
  const pivot = buildScheduleESummaryPivot(transactions);
  downloadScheduleESummaryFromPivot(pivot, yearLabel);
};

/** Schedule E detail ledger columns (transaction-level for audit). */
const SCHEDULE_E_LEDGER_COLUMNS = [
  "Tax Year",
  "Date Received",
  "Due Date",
  "Rent Period",
  "Property Address",
  "Door",
  "Tenant Name",
  "Deposit Account",
  "Category",
  "Description",
  "Amount",
  "Currency",
  "Payment Method",
  "Status",
  "Notes",
  "Payment ID",
  "Transaction ID",
];

const mapTransactionToLedgerRow = (t) => ({
  "Tax Year": t.taxYear,
  "Date Received": t.dateReceived,
  "Due Date": t.dueDate,
  "Rent Period": t.rentPeriod,
  "Property Address": t.propertyAddress || t.propertyOrAddress,
  Door: t.unitName,
  "Tenant Name": t.tenantName,
  "Deposit Account": t.depositAccountName ?? "",
  Category: t.category,
  Description: t.description,
  Amount: t.amount,
  Currency: t.currency || "USD",
  "Payment Method": t.paymentMethod,
  Status: t.status,
  Notes: t.notes || "",
  "Payment ID": t.paymentId,
  "Transaction ID": t.transactionId || "",
});

const downloadDetailLedgerCsv = (transactions, yearLabel, unitIdFilter) => {
  if (!transactions || !transactions.length) {
    message.warning("No transactions to export.");
    return;
  }
  const rows = transactions.map(mapTransactionToLedgerRow);
  const header = SCHEDULE_E_LEDGER_COLUMNS.join(",");
  const csvRows = rows.map((row) =>
    SCHEDULE_E_LEDGER_COLUMNS.map((col) => escapeCsvCell(row[col])).join(",")
  );
  const csv = [header, ...csvRows].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `Schedule-E-Ledger-${yearLabel}-${
    unitIdFilter === "all" ? "all" : unitIdFilter
  }.csv`;
  link.click();
  URL.revokeObjectURL(url);
};

const NO_DATA_MESSAGE =
  "Add properties, doors, and payments to generate a report.";

const TaxSummariesModal = ({
  isOpen,
  onClose,
  payments = [],
  units = [],
  tenants = [],
  properties = [],
  connectAccounts = [],
}) => {
  const currentYear = new Date().getFullYear();
  const isDemoMode = getDebugMode();
  const { isMobile } = useBreakpoints();
  const [taxYear, setTaxYear] = useState(() =>
    isDemoMode ? "2025" : String(currentYear)
  );
  const [unitIdFilter, setUnitIdFilter] = useState("all");
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (isDemoMode && isOpen) setTaxYear("2025");
  }, [isDemoMode, isOpen]);

  const taxYearOptions = useMemo(
    () => [
      { value: "last12", label: "Last 12 Months" },
      { value: String(currentYear), label: String(currentYear) },
      { value: String(currentYear - 1), label: String(currentYear - 1) },
      { value: String(currentYear - 2), label: String(currentYear - 2) },
    ],
    [currentYear]
  );

  const propertyOptions = useMemo(() => {
    if (isDemoMode) {
      return [
        { value: "all", label: "All Properties" },
        { value: "demo-maple", label: "123 Maple Ave" },
        { value: "demo-oak", label: "405 Oak St" },
        { value: "demo-pine", label: "789 Pine Ln" },
      ];
    }
    const list = [{ value: "all", label: "All Properties" }];
    (units || []).forEach((u) => {
      list.push({
        value: u.id,
        label: u.name || getUnitDisplayAddress(u) || u.id,
      });
    });
    return list;
  }, [units, isDemoMode]);

  const data = useMemo(() => {
    if (!isOpen) return null;
    if (isDemoMode) {
      const stats = getDemoTaxSummaryStats(unitIdFilter);
      return {
        ...stats,
        transactions: getDemoTransactions(unitIdFilter),
        taxYearLabel: "2025",
      };
    }
    return buildTaxSummaryFromPayments(
      payments,
      units,
      taxYear,
      unitIdFilter,
      properties,
      connectAccounts
    );
  }, [
    isOpen,
    payments,
    units,
    taxYear,
    unitIdFilter,
    isDemoMode,
    properties,
    connectAccounts,
  ]);

  const yearLabel = taxYear === "last12" ? "Last12Months" : taxYear;

  const handleExportSummary = () => {
    setExporting(true);
    let pivot = null;
    if (isDemoMode) {
      pivot = getDemoTaxPivot(unitIdFilter);
    } else if (data?.transactions?.length) {
      pivot = buildScheduleESummaryPivot(
        data.transactions,
        data.entityByProperty
      );
    }
    if (!pivot?.rows?.length) {
      message.warning("No transactions to export.");
      setExporting(false);
      return;
    }
    const ok = downloadScheduleETaxPacketExcel(pivot, yearLabel);
    if (ok) message.success("Tax Packet downloaded.");
    setExporting(false);
  };

  const handleExportDetailLedger = () => {
    if (isDemoMode) {
      const transactions = getDemoTransactions(unitIdFilter);
      if (!transactions.length) {
        message.warning("No transactions to export.");
        return;
      }
      setExporting(true);
      downloadDetailLedgerCsv(transactions, yearLabel, unitIdFilter);
      message.success("Detail ledger downloaded.");
      setExporting(false);
    } else if (data?.transactions?.length) {
      setExporting(true);
      downloadDetailLedgerCsv(data.transactions, yearLabel, unitIdFilter);
      message.success("Detail ledger downloaded.");
      setExporting(false);
    } else {
      message.warning("No transactions to export.");
    }
  };

  const totalIncome = data?.total_income ?? 0;
  const netRevenue = data?.net_revenue ?? 0;
  const breakdown = data?.breakdown ?? { rent: 0, fees: 0 };
  const hasNoData = !isDemoMode && !data?.transactions?.length;

  return (
    <StyledModal
      title={
        <ModalHeader>
          <AccountBookOutlined
            style={{
              color: "white",
              marginRight: "8px",
              filter: "brightness(0) invert(1)",
            }}
          />
          Tax Summaries (Schedule E)
        </ModalHeader>
      }
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={640}
      centered
      destroyOnClose
    >
      <ModalContent>
        <FiltersCard>
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={12}>
              <Label>Tax year</Label>
              <Select
                value={taxYear}
                onChange={setTaxYear}
                style={{ width: "100%" }}
                size="large"
                options={taxYearOptions}
              />
            </Col>
            <Col xs={24} sm={12}>
              <Label>Property</Label>
              <Select
                value={unitIdFilter}
                onChange={setUnitIdFilter}
                style={{ width: "100%" }}
                size="large"
                options={propertyOptions}
              />
            </Col>
          </Row>
        </FiltersCard>

        <>
          <SummaryRow gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <SummaryCard>
                <Statistic
                  title="Total Income"
                  value={totalIncome}
                  prefix={<DollarOutlined />}
                  valueStyle={{
                    color: "var(--color-primary)",
                    fontWeight: 700,
                    fontSize: "1.5rem",
                  }}
                />
                <Breakdown>
                  Rent: ${breakdown.rent?.toLocaleString?.() ?? 0} · Fees: $
                  {breakdown.fees?.toLocaleString?.() ?? 0}
                </Breakdown>
                <Breakdown>Paid payments only (cash basis)</Breakdown>
              </SummaryCard>
            </Col>
            <Col xs={24} sm={12}>
              <SummaryCard>
                <Statistic
                  title="Net Revenue"
                  value={netRevenue}
                  prefix={<DollarOutlined />}
                  valueStyle={{
                    color: "var(--color-primary)",
                    fontWeight: 700,
                    fontSize: "1.5rem",
                  }}
                />
                <Breakdown>
                  {isDemoMode ? "Income minus expenses" : "After expenses"}
                </Breakdown>
                <Breakdown>
                  {isDemoMode
                    ? "From Schedule E categories"
                    : "Based on tracked income and expenses"}
                </Breakdown>
              </SummaryCard>
            </Col>
          </SummaryRow>

          <ExportCard>
            <ExportTitle>Export for tax filing</ExportTitle>
            <ExportDescription>
              Download a formatted Excel Tax Packet (Schedule E summary) or the
              full transaction ledger for your accountant.
            </ExportDescription>
            {hasNoData && isMobile && (
              <ExportWarning>{NO_DATA_MESSAGE}</ExportWarning>
            )}
            <ExportButtons>
              {hasNoData && !isMobile ? (
                <Tooltip title={NO_DATA_MESSAGE}>
                  <span>
                    <Button
                      type="primary"
                      size="large"
                      icon={
                        <DownloadOutlined style={{ filter: "invert(1)" }} />
                      }
                      loading={exporting}
                      disabled
                    >
                      Download Tax Packet (.xlsx)
                    </Button>
                  </span>
                </Tooltip>
              ) : (
                <Button
                  type="primary"
                  size="large"
                  icon={<DownloadOutlined style={{ filter: "invert(1)" }} />}
                  onClick={handleExportSummary}
                  loading={exporting}
                  disabled={hasNoData}
                >
                  Download Tax Packet (.xlsx)
                </Button>
              )}
              {hasNoData && !isMobile ? (
                <Tooltip title={NO_DATA_MESSAGE}>
                  <span>
                    <Button
                      size="large"
                      icon={<DownloadOutlined />}
                      loading={exporting}
                      disabled
                    >
                      Download Detail Ledger
                    </Button>
                  </span>
                </Tooltip>
              ) : (
                <Button
                  size="large"
                  icon={<DownloadOutlined />}
                  onClick={handleExportDetailLedger}
                  loading={exporting}
                  disabled={hasNoData}
                >
                  Download Detail Ledger
                </Button>
              )}
            </ExportButtons>
          </ExportCard>
        </>
      </ModalContent>
    </StyledModal>
  );
};

export default TaxSummariesModal;

const StyledModal = styled(Modal)`
  .ant-modal-header {
    background: linear-gradient(135deg, var(--color-primary) 0%, #4a7c59 100%);
    border-bottom: none;
    border-radius: 12px 12px 0 0;
    padding: 16px 20px;
  }

  .ant-modal-title {
    color: white !important;
    font-weight: 600;
    font-size: 18px;
    line-height: 1.2;
  }

  .ant-modal-close {
    color: white !important;
    font-size: 16px;
    top: 16px;
    right: 20px;

    &:hover {
      color: rgba(255, 255, 255, 0.8) !important;
    }
  }

  .ant-modal-close .ant-modal-close-x,
  .ant-modal-close .ant-modal-close-icon,
  .ant-modal-close .anticon,
  .ant-modal-close svg,
  .ant-modal-close:hover .ant-modal-close-x,
  .ant-modal-close:hover .ant-modal-close-icon,
  .ant-modal-close:hover .anticon,
  .ant-modal-close:hover svg {
    color: #ffffff !important;
    fill: #ffffff !important;
  }

  .ant-modal-body {
    padding: 24px;
    background: #fff;
    overflow-x: hidden;
  }

  .ant-select-arrow {
    position: absolute !important;
    top: 50% !important;
    transform: translateY(-50%) !important;
    margin: 0 !important;
  }

  .ant-modal-content {
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
    max-width: 100vw;
    padding: 0;
  }

  .ant-modal {
    margin: 20px auto;
  }

  @media (max-width: 768px) {
    .ant-modal-body {
      padding: 16px;
    }
    .ant-modal-header {
      padding: 12px 16px;
    }
    .ant-modal-title {
      font-size: 16px;
    }
    .ant-modal-close {
      top: 12px;
      right: 16px;
      font-size: 14px;
    }
  }

  @media (max-width: 480px) {
    .ant-modal-body {
      padding: 12px;
    }
    .ant-modal-header {
      padding: 10px 12px;
    }
    .ant-modal-title {
      font-size: 15px;
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  font-size: 18px;
  font-weight: 600;
  color: white;
`;

const ModalContent = styled.div`
  padding: 0.5rem 0;
`;

const Label = styled.div`
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--color-text-light);
  margin-bottom: 0.5rem;
`;

const FiltersCard = styled(Card)`
  margin-bottom: 1rem;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
`;

const SummaryRow = styled(Row)`
  margin-bottom: 1rem;
`;

const SummaryCard = styled(Card)`
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  height: 100%;

  .ant-statistic-title {
    color: var(--color-text-light);
    font-size: 0.9rem;
  }
`;

const Breakdown = styled.div`
  margin-top: 0.5rem;
  font-size: 0.8rem;
  color: var(--color-text-light);
`;

const ExportCard = styled(Card)`
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
`;

const ExportTitle = styled.div`
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
`;

const ExportDescription = styled.div`
  font-size: 0.85rem;
  color: var(--color-text-light);
  margin-bottom: 1rem;
`;

const ExportWarning = styled.div`
  font-size: 0.875rem;
  color: var(--color-warning, #d46b08);
  margin-bottom: 1rem;
  padding: 0.5rem 0.75rem;
  background: rgba(212, 107, 8, 0.08);
  border-radius: 6px;
`;

const ExportButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
`;
